/**
 * SearchableSelect Component
 * 검색 가능한 드롭다운 셀렉트 컴포넌트
 * - 우측 화살표 클릭: 전체 목록 드롭다운
 * - 입력 영역 클릭: 직접 입력 및 자동완성
 * - Portal을 사용하여 부모 overflow에 영향받지 않음
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

export interface SelectOption {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    showAllOption?: boolean;
    allOptionLabel?: string;
}

interface DropdownPosition {
    top: number;
    left: number;
    width: number;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = '선택 또는 검색...',
    className = '',
    disabled = false,
    showAllOption = true,
    allOptionLabel = '전체'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // 선택된 옵션의 라벨 표시
    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    // 필터링된 옵션
    const filteredOptions = useCallback(() => {
        const allOptions = showAllOption
            ? [{ value: '', label: allOptionLabel }, ...options]
            : options;

        if (!searchTerm) return allOptions;

        return allOptions.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm, showAllOption, allOptionLabel]);

    // 드롭다운 위치 계산
    const updateDropdownPosition = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    }, []);

    // 드롭다운이 열릴 때 위치 계산
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            // 스크롤이나 리사이즈 시 위치 업데이트
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);
            return () => {
                window.removeEventListener('scroll', updateDropdownPosition, true);
                window.removeEventListener('resize', updateDropdownPosition);
            };
        }
    }, [isOpen, updateDropdownPosition]);

    // 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // containerRef와 listRef 둘 다 확인
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                listRef.current &&
                !listRef.current.contains(target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 하이라이트된 항목 스크롤
    useEffect(() => {
        if (isOpen && highlightedIndex >= 0 && listRef.current) {
            const highlightedItem = listRef.current.children[highlightedIndex] as HTMLElement;
            if (highlightedItem) {
                highlightedItem.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, isOpen]);

    // 옵션 선택
    const handleSelect = useCallback((optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
    }, [onChange]);

    // 입력 변경
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        setHighlightedIndex(-1);
    };

    // 입력 영역 클릭
    const handleInputClick = () => {
        if (!disabled) {
            setIsOpen(true);
            inputRef.current?.focus();
        }
    };

    // 드롭다운 버튼 클릭
    const handleDropdownClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            setIsOpen(!isOpen);
            setSearchTerm('');
            if (!isOpen) {
                inputRef.current?.focus();
            }
        }
    };

    // 키보드 네비게이션
    const handleKeyDown = (e: React.KeyboardEvent) => {
        const filtered = filteredOptions();

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev =>
                        prev < filtered.length - 1 ? prev + 1 : prev
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0 && filtered[highlightedIndex]) {
                    handleSelect(filtered[highlightedIndex].value);
                } else if (filtered.length === 1) {
                    handleSelect(filtered[0].value);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchTerm('');
                setHighlightedIndex(-1);
                break;
            case 'Tab':
                setIsOpen(false);
                setSearchTerm('');
                break;
        }
    };

    // 클리어 버튼
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange('');
        setSearchTerm('');
        inputRef.current?.focus();
    };

    const filtered = filteredOptions();

    // 드롭다운 Portal 렌더링
    const renderDropdown = () => {
        if (!isOpen) return null;

        const dropdown = (
            <ul
                ref={listRef}
                className="searchable-select-options searchable-select-portal"
                style={{
                    position: 'fixed',
                    top: dropdownPosition.top - window.scrollY,
                    left: dropdownPosition.left,
                    width: dropdownPosition.width
                }}
            >
                {filtered.length > 0 ? (
                    filtered.map((option, index) => (
                        <li
                            key={option.value || '_all'}
                            className={`searchable-select-option ${option.value === value ? 'selected' : ''} ${index === highlightedIndex ? 'highlighted' : ''}`}
                            onClick={() => handleSelect(option.value)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            {option.label}
                        </li>
                    ))
                ) : (
                    <li className="searchable-select-no-results">
                        검색 결과가 없습니다
                    </li>
                )}
            </ul>
        );

        return createPortal(dropdown, document.body);
    };

    return (
        <div
            ref={containerRef}
            className={`searchable-select ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        >
            <div className="searchable-select-input-container" onClick={handleInputClick}>
                <input
                    ref={inputRef}
                    type="text"
                    className="searchable-select-input"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={value ? '' : placeholder}
                    disabled={disabled}
                    autoComplete="off"
                />
                <div className="searchable-select-actions">
                    {value && !disabled && (
                        <button
                            type="button"
                            className="searchable-select-clear"
                            onClick={handleClear}
                            tabIndex={-1}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                    <button
                        type="button"
                        className="searchable-select-dropdown-btn"
                        onClick={handleDropdownClick}
                        tabIndex={-1}
                        disabled={disabled}
                    >
                        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}></i>
                    </button>
                </div>
            </div>

            {renderDropdown()}
        </div>
    );
};

export default SearchableSelect;
